import { useQuery } from '@tanstack/react-query';
import { EmployeesService } from '@/api';
import type { employees_EmployeeAuditRecord } from '@/types';
import { createAuditHistoryHook, createInvalidateAuditQueries, createAuditQueryKeys } from './useGenericAudit';

// Create employee audit history hook using the generic factory
export const useEmployeeAuditHistory = createAuditHistoryHook<employees_EmployeeAuditRecord>({
  entityType: 'employees',
  apiFunction: (entityId: string, page: number, limit: number) => 
    EmployeesService.getApiV1EmployeesHistory(entityId, page, limit),
});

// Create invalidation function for employee audit queries
export const invalidateEmployeeAuditQueries = createInvalidateAuditQueries('employees');

// Additional hooks for field-specific and recent changes functionality
// These follow the same pattern but use different API endpoints

// Get query keys for employees
const employeeAuditKeys = createAuditQueryKeys('employees');

// Hook to fetch field-specific history for an employee
export function useEmployeeFieldHistory(
  employeeId: string,
  fieldName: string,
  options?: {
    enabled?: boolean;
  }
) {
  const { enabled = true } = options || {};

  return useQuery<employees_EmployeeAuditRecord[]>({
    queryKey: employeeAuditKeys.fieldHistory(employeeId, fieldName),
    queryFn: async () => {
      const result = await EmployeesService.getApiV1EmployeesHistoryField(employeeId, fieldName);
      return result;
    },
    enabled: enabled && !!employeeId && !!fieldName,
    staleTime: 30000, // 30 seconds
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors
      if (error instanceof Error && (
        error.message.includes('401') || 
        error.message.includes('403') || 
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

// Hook to fetch recent changes for an employee
export function useEmployeeRecentChanges(
  employeeId: string,
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
  }
) {
  const { enabled = true, refetchInterval } = options || {};

  return useQuery<employees_EmployeeAuditRecord[]>({
    queryKey: employeeAuditKeys.recentChanges(employeeId),
    queryFn: () => EmployeesService.getApiV1EmployeesHistoryRecent(employeeId),
    enabled: enabled && !!employeeId,
    staleTime: 60000, // 1 minute
    refetchInterval, // Optional auto-refresh for recent changes
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors
      if (error instanceof Error && (
        error.message.includes('401') || 
        error.message.includes('403') || 
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}